import { Dispatch, FC, SetStateAction, useRef, useState } from "react";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  ColumnsPanelTrigger,
  GridCheckIcon,
  GridDensity,
  GridRowSelectionModel,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid-pro";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import __ from "src/utils/Translations";
import { TableSettings } from "./tables.models";

interface ICustomToolbarProps {
  rowSelectionModel?: GridRowSelectionModel;
  settings: TableSettings;
  showFilters: boolean;
  setSettings: Dispatch<SetStateAction<TableSettings>>;
  setShowFilters: Dispatch<SetStateAction<boolean>>;
  onBatchRejection?: VoidFunction;
  onBatchAcceptance?: VoidFunction;
}

const DENISTY_OPTIONS: { label: string; value: GridDensity }[] = [
  { label: "Widok standardowy", value: "standard" },
  { label: "Widok komfortowy", value: "comfortable" },
];

const CustomToolbar: FC<ICustomToolbarProps> = ({
  rowSelectionModel,
  settings,
  showFilters,
  setSettings,
  setShowFilters,
  onBatchRejection,
  onBatchAcceptance,
}) => {
  const settingsMenuTriggerRef = useRef<HTMLButtonElement>(null);

  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);

  return (
    <Toolbar>
      <Stack
        direction="row"
        sx={{ justifyContent: "end", width: "100%", mr: "1rem" }}
      >
        {!!rowSelectionModel?.ids.size && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: "end", width: "100%", mr: "1rem" }}
          >
            <Button
              size="small"
              color="error"
              startIcon={<CancelOutlinedIcon />}
              variant="outlined"
              onClick={onBatchRejection}
            >
              {__("Odrzuć zaznaczone")}
            </Button>
            <Button
              size="small"
              color="success"
              startIcon={<CheckCircleOutlinedIcon />}
              variant="outlined"
              onClick={onBatchAcceptance}
            >
              {__("Zaakceptuj zaznaczone")}
            </Button>
          </Stack>
        )}
      </Stack>

      <Tooltip title={__("Filtry")}>
        <ToolbarButton
          onClick={() => setShowFilters((value: boolean) => !value)}
        >
          {showFilters ? <FilterAltOffIcon /> : <FilterAltIcon />}
        </ToolbarButton>
      </Tooltip>
      <Tooltip title={__("Kolumny")}>
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon />
        </ColumnsPanelTrigger>
      </Tooltip>
      <Tooltip title={__("Ustawienia")}>
        <ToolbarButton
          ref={settingsMenuTriggerRef}
          id="settings-menu-trigger"
          aria-controls="settings-menu"
          aria-haspopup="true"
          aria-expanded={settingsMenuOpen ? "true" : undefined}
          onClick={() => setSettingsMenuOpen(true)}
        >
          <SettingsIcon />
        </ToolbarButton>
      </Tooltip>
      <Menu
        id="settings-menu"
        anchorEl={settingsMenuTriggerRef.current}
        open={settingsMenuOpen}
        onClose={() => setSettingsMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableAutoFocusItem
        disableScrollLock
        slotProps={{
          list: {
            "aria-labelledby": "settings-menu-trigger",
          },
        }}
      >
        {DENISTY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() =>
              setSettings((currentSettings) => ({
                ...currentSettings,
                density: option.value,
              }))
            }
          >
            <ListItemIcon>
              {settings.density === option.value && (
                <GridCheckIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Toolbar>
  );
};

export default CustomToolbar;
